import React, { useEffect, useState } from 'react';
import {
  Plus, Trash2, Upload, Link as LinkIcon, Image as ImageIcon, 
  Loader2, Save, ArrowRight, LayoutTemplate
} from 'lucide-react';

interface AdBanner {
  id: number;
  imageUrl: string;
  linkUrl: string;
}

const BannerManagement: React.FC = () => {
  const [banners, setBanners] = useState<AdBanner[]>([]);
  const [newBanner, setNewBanner] = useState<{
    imageFile: File | null;
    linkUrl: string;
  }>({
    imageFile: null,
    linkUrl: '',
  });

  const [preview, setPreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const API_BASE_URL = '/nodejs';

  const fetchBanners = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/banners`);
      const data = await res.json();
      setBanners(data);
    } catch {
      setError('Failed to load banners');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setNewBanner({ ...newBanner, imageFile: file });

    if (file) {
      setPreview(URL.createObjectURL(file));
    } else {
      setPreview(null);
    }
  };

  const handleAddBanner = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBanner.imageFile) return;

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append('image', newBanner.imageFile);
    formData.append('linkUrl', newBanner.linkUrl);

    try {
      await fetch(`${API_BASE_URL}/api/banners`, {
        method: 'POST',
        body: formData,
      });
      fetchBanners();
      setNewBanner({ imageFile: null, linkUrl: '' });
      setPreview(null);
    } catch {
      setError('Failed to add banner');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveBanner = async (id: number) => {
    if (!window.confirm('Delete this banner permanently?')) return;
    try {
      await fetch(`${API_BASE_URL}/api/banners/${id}`, { method: 'DELETE' });
      setBanners(banners.filter(b => b.id !== id));
    } catch (e) { console.error(e); }
  };

  return (
    <div className="min-h-screen bg-white font-sans animate-fade-in-up">
      <div className="max-w-[1600px] mx-auto px-6 py-12">

        {/* Header */}
        <div className="mb-12 border-b border-gray-100 pb-6 flex items-center gap-5">
          <div className="w-14 h-14 bg-[#006d5b]/10 rounded-2xl flex items-center justify-center text-[#006d5b] shadow-sm">
             <LayoutTemplate size={28} />
          </div>
          <div>
            <h1 className="text-3xl font-black text-gray-900 tracking-tight">Home Banners</h1>
            <p className="text-gray-500 font-medium mt-1 text-sm">Manage rotating slides for the main landing page.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          
          {/* LEFT: EDITOR PANEL (4 Columns) */}
          <div className="lg:col-span-4 space-y-8">
            <div className="bg-white rounded-[30px] border border-gray-200 shadow-xl shadow-gray-100/50 overflow-hidden sticky top-6">
               
               <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                  <h3 className="text-sm font-black text-gray-800 uppercase tracking-widest flex items-center gap-2">
                    <Plus size={16} className="text-[#006d5b]"/> Add New Slide
                  </h3>
               </div>

               <form onSubmit={handleAddBanner} className="p-6 space-y-6">
                  
                  {/* Image Upload */}
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-2 block">Banner Image</label>
                    <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 hover:border-[#006d5b] hover:bg-[#006d5b]/5 transition-all text-center group cursor-pointer relative bg-gray-50/30">
                       <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                          required
                       />
                       <div className="flex flex-col items-center justify-center gap-3">
                          {preview ? (
                            <div className="w-full h-32 rounded-lg overflow-hidden relative shadow-sm border border-gray-200">
                               <img src={preview} className="w-full h-full object-cover" alt="Preview" />
                               <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white font-bold text-xs">
                                  Click to Change
                               </div>
                            </div>
                          ) : (
                            <>
                              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-gray-400 group-hover:text-[#006d5b] transition-colors shadow-sm border border-gray-100">
                                <Upload size={24} />
                              </div>
                              <div>
                                <p className="text-xs font-black text-gray-700">Upload Creative</p>
                                <p className="text-[10px] text-gray-400 font-bold mt-0.5">1920x600 Rec.</p>
                              </div>
                            </>
                          )}
                       </div>
                    </div>
                  </div>

                  {/* Link URL */}
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-2 block">Redirect Link</label>
                    <div className="relative">
                      <LinkIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="text"
                        value={newBanner.linkUrl}
                        onChange={(e) => setNewBanner({ ...newBanner, linkUrl: e.target.value })}
                        placeholder="https://example.com/promo"
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-11 pr-4 py-3 text-sm font-bold text-gray-900 focus:bg-white focus:border-[#006d5b] outline-none transition-all placeholder:text-gray-400"
                        required
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#006d5b] hover:bg-[#005a4b] text-white py-3.5 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg shadow-[#006d5b]/20 flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-70"
                  >
                    {isSubmitting ? (
                        <>Uploading <Loader2 className="animate-spin" size={16}/></>
                    ) : (
                        <>Publish Banner <ArrowRight size={16}/></>
                    )}
                  </button>

               </form>
            </div>
          </div>

          {/* RIGHT: GALLERY PANEL (8 Columns) */}
          <div className="lg:col-span-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-sm font-black text-gray-400 uppercase tracking-widest">Active Slides</h3>
              <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-[10px] font-black uppercase">
                 {banners.length} Live
              </span>
            </div>

            {isLoading ? (
               <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#006d5b]" size={32}/></div>
            ) : banners.length === 0 ? (
               <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-gray-100 rounded-[30px] bg-gray-50/50">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 text-gray-300 shadow-sm">
                     <ImageIcon size={32} />
                  </div>
                  <h3 className="text-lg font-black text-gray-900">No Banners Found</h3>
                  <p className="text-gray-400 text-sm font-medium mt-1">Upload your first homepage banner.</p>
               </div>
            ) : (
               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 {banners.map((banner) => (
                   <div key={banner.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-xl hover:shadow-gray-200/50 hover:border-gray-200 transition-all group relative">
                     
                     {/* Banner Image */}
                     <div className="h-48 w-full bg-gray-100 relative">
                       <img
                         src={`${API_BASE_URL}${banner.imageUrl}`}
                         className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                         alt="Banner"
                       />
                       
                       {/* Overlay Gradient */}
                       <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                          <div className="w-full flex justify-between items-center text-white">
                             <div className="flex items-center gap-2 text-xs font-bold truncate pr-4">
                                <LinkIcon size={12}/> {banner.linkUrl}
                             </div>
                          </div>
                       </div>

                       {/* Delete Button (Always visible on mobile, hover on desktop) */}
                       <button
                         onClick={() => handleRemoveBanner(banner.id)}
                         className="absolute top-3 right-3 bg-white/90 hover:bg-red-500 hover:text-white text-red-500 p-2 rounded-lg shadow-sm backdrop-blur-sm transition-all md:opacity-0 md:group-hover:opacity-100"
                         title="Remove Banner"
                       >
                         <Trash2 size={16} />
                       </button>
                     </div>
                   </div>
                 ))}
               </div>
            )}
            
            {error && (
               <div className="mt-6 p-4 bg-red-50 text-red-600 rounded-xl text-sm font-bold text-center border border-red-100">
                  {error}
               </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default BannerManagement;